import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import siteConfig from '../config/siteContent';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    interest: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/send`, formData);
      
      toast({
        title: "✅ Message Sent Successfully!",
        description: "Thank you for contacting us. We'll respond within 24 hours.",
        duration: 5000,
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        interest: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "❌ Error Sending Message",
        description: "Please try again or email us directly at info@reposehotels.com.au",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Location',
      details: [`${siteConfig.contact.location.city}, ${siteConfig.contact.location.state}`, siteConfig.contact.location.country]
    },
    {
      icon: Mail,
      title: 'Email',
      details: [siteConfig.contact.email]
    },
    {
      icon: Phone,
      title: 'Phone',
      details: [siteConfig.contact.phoneDisplay]
    }
  ];

  const interests = [
    'Hotel Acquisition Advisory',
    'Asset Management Services',
    'Franchise Operations',
    'Investment Partnership',
    'Strategic Consulting',
    'General Inquiry'
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1536269404660-0a8d4e88bf1b)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        </div>
        
        <div className="relative h-full flex flex-col items-center justify-center text-white px-8 text-center pt-24">
          <h1 className="font-playfair text-5xl md:text-7xl font-bold mb-6 tracking-wide">
            Get in Touch
          </h1>
          <p className="text-xl md:text-2xl font-light max-w-3xl">
            Let's discuss how we can help transform your hospitality investment
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <h2 className="font-playfair text-3xl font-bold text-[#2B2B2B] mb-8">
                Contact Information
              </h2>
              <p className="text-lg text-[#3A2E24] mb-8 leading-relaxed">
                We welcome the opportunity to speak with you about your hotel investment or management needs.
              </p>

              <div className="space-y-6 mb-12">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#B89C4A] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#2B2B2B] mb-2">{info.title}</h3>
                        {info.details.map((detail, idx) => (
                          <p key={idx} className="text-[#3A2E24]">{detail}</p>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <Card className="p-6 bg-[#F4F4F4] border-none">
                <h3 className="font-playfair text-xl font-bold text-[#2B2B2B] mb-4">
                  Office Hours
                </h3>
                <div className="space-y-2 text-[#3A2E24]">
                  <p><span className="font-semibold">Monday - Friday:</span> {siteConfig.contact.officeHours.weekdays}</p>
                  <p><span className="font-semibold">Saturday - Sunday:</span> {siteConfig.contact.officeHours.weekend}</p>
                </div>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="p-8 md:p-12 shadow-xl">
                <h2 className="font-playfair text-3xl font-bold text-[#2B2B2B] mb-8">
                  Send Us a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-[#2B2B2B] mb-2">
                        Full Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full"
                        placeholder="John Smith"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-[#2B2B2B] mb-2">
                        Email Address *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-[#2B2B2B] mb-2">
                        Phone Number
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full"
                        placeholder="+61 XXX XXX XXX"
                      />
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-semibold text-[#2B2B2B] mb-2">
                        Company Name
                      </label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full"
                        placeholder="Your Company"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="interest" className="block text-sm font-semibold text-[#2B2B2B] mb-2">
                      Area of Interest *
                    </label>
                    <select
                      id="interest"
                      name="interest"
                      value={formData.interest}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#B89C4A] bg-white"
                    >
                      <option value="">Select an option</option>
                      {interests.map((interest, index) => (
                        <option key={index} value={interest}>{interest}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-[#2B2B2B] mb-2">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full resize-none"
                      placeholder="Tell us about your project or inquiry..."
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="bg-[#B89C4A] hover:bg-[#A58B3A] text-white px-8 py-6 text-lg w-full md:w-auto transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'} <Send className="ml-2 w-5 h-5" />
                  </Button>

                  <p className="text-sm text-[#3A2E24] mt-4">
                    * Required fields. We respect your privacy and will never share your information.
                  </p>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Contact Us */}
      <section className="py-32 px-8 bg-[#F4F4F4]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-6">
              What to Expect
            </h2>
            <p className="text-xl text-[#3A2E24] max-w-3xl mx-auto">
              When you reach out to us, here's what happens next
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-[#B89C4A] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl font-bold">1</span>
              </div>
              <h3 className="font-playfair text-xl font-bold text-[#2B2B2B] mb-4">
                Initial Response
              </h3>
              <p className="text-[#3A2E24]">
                We'll respond within 24 hours to acknowledge your inquiry and schedule a conversation.
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-[#B89C4A] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl font-bold">2</span>
              </div>
              <h3 className="font-playfair text-xl font-bold text-[#2B2B2B] mb-4">
                Discovery Call
              </h3>
              <p className="text-[#3A2E24]">
                We'll have an in-depth discussion to understand your needs and explore how we can help.
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-[#B89C4A] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl font-bold">3</span>
              </div>
              <h3 className="font-playfair text-xl font-bold text-[#2B2B2B] mb-4">
                Proposal
              </h3>
              <p className="text-[#3A2E24]">
                If there's a fit, we'll prepare a detailed proposal outlining our recommended approach.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-24 px-8 bg-[#2B2B2B] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-6">
            Prefer to Talk Directly?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Call us during business hours or schedule a video consultation at your convenience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${siteConfig.contact.phone}`} className="inline-block">
              <Button 
                size="lg" 
                className="bg-[#B89C4A] hover:bg-[#A58B3A] text-white px-8 transition-all duration-300 hover:scale-105"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call Us Now
              </Button>
            </a>
            <a href={`mailto:${siteConfig.contact.email}`} className="inline-block">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-[#2B2B2B] px-8 transition-all duration-300 hover:scale-105"
              >
                <Mail className="mr-2 w-5 h-5" />
                Email Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
